// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonSize,
  ButtonVariant,
  Icon,
  IconName,
  ModalCloseButton,
  Popover,
  Text,
  Tooltip,
} from '@usealloy/component-library';

import { selectFocusedAction, selectWorkflowData } from 'flow/store/redux/selectors';
import { updateAction } from 'flow/store/redux/slice';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import type { ValueOf } from '~/helpers/typescript';

import { ActionDataType } from '../AxisHeaderDropdown';
import type { Axis } from '../AxisHeaderDropdown';
import type { AxisOptions } from './types';
import {
  AxisOrderOption, AxisTypeOption,
} from './types';
import {
  AxisConfigTrigger,
  OptionGroup, PopoverContainer, PopoverContent,
  PopoverFooter, StyledBox, StyledChip,
} from './styles';
import {
  getAxisOptions, updateAxisSettings,
  updateMatrixSizeToMatchHeaders,
} from './helpers';
import type { Action, MatrixModel } from '~/dashboard/views/workflows/store/engine/types';
import { getAxisDataRouteKey } from '../AxisHeaderDropdown/helpers';
import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';
import { HighRangeOption, LowRangeOption } from 'flow/service/actions';
import { useMatrixHistoryDispatch } from '../useMatrixHistoryDispatch';
import { getMatrixValueHtmlElementId } from 'flow/editor/hooks/useWorkflowHistory/helpers';

export interface AxisConfigPopoverProps {
  axis: Axis;
  isDisabled?: boolean;
  // Optional action prop - if not provided, uses the focused action from Redux
  action?: Action;
}

export default function AxisConfigPopover({
  axis, isDisabled = false, action,
}: AxisConfigPopoverProps) {
  const dispatch = useAppDispatch();
  const focusedAction = useAppSelector(selectFocusedAction);

  // Use the provided action prop if available, otherwise fall back to focused action
  const sourceAction = action || focusedAction;
  const { publicId, matrixModel } = sourceAction;
  const { actions, dataRoutes } = useAppSelector(selectWorkflowData);
  const handleDispatch = useMatrixHistoryDispatch(publicId);

  const axisDataRouteKey = getAxisDataRouteKey(axis);
  const dataRouteId = matrixModel[axisDataRouteKey];
  const dataRoute = dataRoutes[dataRouteId];
  const targetAction = actions[dataRoute?.sourceId];
  const axisSettings = useMemo(
    () => matrixModel.axis[axis.toLowerCase()], [axis, matrixModel.axis],
  );

  const axisAttrDataType = targetAction?.type === ActionType.INPUT
    ? targetAction.input.dataType
    : null;

  const isNotNumberDataType = (axisAttrDataType !== ActionDataType.INTEGER)
    && (axisAttrDataType !== ActionDataType.DECIMAL);

  const initialOptions = useMemo(() => getAxisOptions(axisSettings), [axisSettings]);
  const [selectedOptions, setSelectedOptions] = useState<AxisOptions>(initialOptions);

  // Popover component is not recreated when switching between different
  // matrix models, so we need to use an useEffect to update the state
  useEffect(() => setSelectedOptions(initialOptions), [initialOptions]);

  const handleOnChange = (
    key: keyof AxisOptions, option: ValueOf<AxisOptions>,
  ) => {
    setSelectedOptions({ ...selectedOptions, [key]: option });
  };

  const isSaveDisabled = useMemo(() => {
    if (selectedOptions.axisType === AxisTypeOption.exact) {
      return false;
    }
    if (selectedOptions.axisType === AxisTypeOption.range) {
      return !Object.values(selectedOptions).every(option => option);
    }
    return true;
  }, [selectedOptions]);

  const handleOnCancel = () => setSelectedOptions(initialOptions);

  const handleOnSave = () => {
    const path = `${publicId}.matrixModel`;
    const axisKey = axis.toLowerCase();

    const updatedAxis = updateAxisSettings({
      axisHeader: axisSettings.axisHeader, initialOptions, selectedOptions,
    });

    const updatedMatrix = updateMatrixSizeToMatchHeaders({
      axis, axisSettings: updatedAxis, matrix: matrixModel.matrix,
    });

    const updatedMatrixModel: MatrixModel = {
      ...matrixModel,
      axis: { ...matrixModel.axis, [axisKey]: updatedAxis },
      matrix: updatedMatrix,
    };

    handleDispatch({
      stateChange: () => dispatch(updateAction({ path, value: updatedMatrixModel })),
      focusTarget: { configPopover: axis },
    });
  };

  const isExactAxisTypeSelected = selectedOptions?.axisType === AxisTypeOption.exact;

  return (
    <Box width="auto">
      <Popover
        trigger={(
          <AxisConfigTrigger
            data-dd-action-name={`matrix-axis-config-${axis.toLowerCase()}`}
            id={getMatrixValueHtmlElementId({ configPopover: axis })}
            tabIndex={-1}
          >
            <Tooltip content={`${axis} Axis Settings`} side="top">
              <Button
                variant={ButtonVariant.tertiary}
                icon={IconName.Settings}
                disabled={isDisabled}
              />
            </Tooltip>
          </AxisConfigTrigger>
        )}
        align="center"
        side="right"
      >
        <PopoverContainer>
          <Text as="h1" size={3} weight={500}>
            {`${axis} Axis Settings`}
          </Text>
          <PopoverContent>
            <OptionGroup>
              Axis Type
              <StyledBox>
                <StyledChip
                  disabled={axisAttrDataType && isNotNumberDataType}
                  onChange={() => handleOnChange('axisType', AxisTypeOption.range)}
                  selected={selectedOptions?.axisType === AxisTypeOption.range}
                  type="radio"
                  name={AxisTypeOption.range}
                  value={AxisTypeOption.range}
                  autoFocus={selectedOptions?.axisType === AxisTypeOption.range}
                >
                  Integer Range (<b>0</b> {' <= X < '} <b>1</b>)
                </StyledChip>
                <StyledChip
                  onChange={() => handleOnChange('axisType', AxisTypeOption.exact)}
                  selected={isExactAxisTypeSelected}
                  type="radio"
                  name={AxisTypeOption.exact}
                  autoFocus={selectedOptions?.axisType === AxisTypeOption.exact}
                >
                  Exact Value (<b>1</b> or <b>high</b>)
                </StyledChip>
              </StyledBox>
            </OptionGroup>
            <OptionGroup>
              Axis Order
              <StyledBox>
                <StyledChip
                  disabled={isExactAxisTypeSelected}
                  hasIcon
                  onChange={() => handleOnChange('axisOrder', AxisOrderOption.asc)}
                  selected={selectedOptions?.axisOrder === AxisOrderOption.asc}
                  type="radio"
                  name={AxisOrderOption.asc}
                >
                  <Box justifyContent="flex-start" gap={2}>
                    <Icon icon={IconName.ArrowRight} />
                    Ascending
                  </Box>
                </StyledChip>
                <StyledChip
                  disabled={isExactAxisTypeSelected}
                  hasIcon
                  onChange={() => handleOnChange('axisOrder', AxisOrderOption.desc)}
                  selected={selectedOptions?.axisOrder === AxisOrderOption.desc}
                  type="radio"
                  name={AxisOrderOption.desc}
                >
                  <Box justifyContent="flex-start" gap={2}>
                    <Icon icon={IconName.ArrowLeft} />
                    Descending
                  </Box>
                </StyledChip>
              </StyledBox>
            </OptionGroup>
            <OptionGroup>
              High Range Setting
              <StyledBox>
                <StyledChip
                  disabled={isExactAxisTypeSelected}
                  onChange={() => handleOnChange('highRange', HighRangeOption.unbounded)}
                  selected={selectedOptions?.highRange === HighRangeOption.unbounded}
                  type="radio"
                  name={HighRangeOption.unbounded}
                >
                  Unbounded (<b>value</b> {' > '} X)
                </StyledChip>
                <StyledChip
                  disabled={isExactAxisTypeSelected}
                  onChange={() => handleOnChange('highRange', HighRangeOption.bounded)}
                  selected={selectedOptions?.highRange === HighRangeOption.bounded}
                  type="radio"
                  name={HighRangeOption.bounded}
                >
                  Bounded (<b>value</b> {' <= X < '} <b>value</b>)
                </StyledChip>
              </StyledBox>
            </OptionGroup>
            <OptionGroup>
              Low Range Setting
              <StyledBox>
                <StyledChip
                  disabled={isExactAxisTypeSelected}
                  onChange={() => handleOnChange('lowRange', LowRangeOption.unbounded)}
                  selected={selectedOptions?.lowRange === LowRangeOption.unbounded}
                  type="radio"
                  name={LowRangeOption.unbounded}
                >
                  Unbounded (<b>value</b> {' <= '} X)
                </StyledChip>
                <StyledChip
                  disabled={isExactAxisTypeSelected}
                  onChange={() => handleOnChange('lowRange', LowRangeOption.bounded)}
                  selected={selectedOptions?.lowRange === LowRangeOption.bounded}
                  type="radio"
                  name={LowRangeOption.bounded}
                >
                  Bounded (<b>value</b> {' <= X < '} <b>value</b>)
                </StyledChip>
              </StyledBox>
            </OptionGroup>
          </PopoverContent>
          <PopoverFooter>
            <Popover.Close asChild>
              <Button onClick={handleOnCancel} data-dd-action-name="matrix-axis-config-cancel">
                Cancel
              </Button>
            </Popover.Close>
            <Popover.Close asChild>
              <Button
                data-dd-action-name="matrix-axis-config-save"
                disabled={isSaveDisabled}
                onClick={handleOnSave}
                variant={ButtonVariant.primary}
              >
                Save
              </Button>
            </Popover.Close>
          </PopoverFooter>
          <Popover.Close asChild>
            <ModalCloseButton
              data-dd-action-name="matrix-axis-config-cancel"
              onClick={handleOnCancel}
              size={ButtonSize.small}
            />
          </Popover.Close>
        </PopoverContainer>
      </Popover>
    </Box>
  );
}
